/**
 * Krok MAP3b — EXPORT MAPY A PREHLEDU PARCELY z panelu parcely (admin).
 *
 * `E4` PNG je HOTOVY SERVEROVY VYREZ (K2) se `download=1`: dlazdice CUZK
 * jsou cizi puvod bez CORS, takze slozit obrazek z platna OpenLayers by
 * skoncilo na „tainted canvas" (`canvas.toDataURL()` vyhodi SecurityError).
 * `J1` PDF sklada server z RADKU PANELU, ktere mu posle tahle cesta —
 * dokument je prevod toho, co makler vidi (vzor O5b), ne druhy vypocet.
 *
 * Binarni odpovedi chodi mimo `apiClient` (ten cte vzdy JSON) — stejny vzor
 * jako `downloadContractPdf` v `contracts-api.ts`.
 */

import { mapExportFileName, type MapExportExtension } from '@reos/map';
import { API_BASE_URL } from './api.js';
import { mediaSrc } from './properties.js';

export interface ParcelOverviewRowInput {
  readonly label: string;
  readonly value: string;
}

/** Jmeno souboru ke stazeni — sdilena pravidla z `@reos/map`. */
export function parcelExportFileName(
  parcelLabel: string | null,
  extension: MapExportExtension,
  /* `Date.now()` (ne `new Date()`, eslint hlida testovatelny cas) — jmeno
     souboru nese den exportu; volajici ho smi predat kvuli testu. */
  today = new Date(Date.now()).toISOString(),
): string {
  return mapExportFileName({ parcelLabel, extension, today });
}

/**
 * Adresa exportu vyrezu do PNG. Mir na API, NE na S3 (poucení UXP4a bod 1:
 * podepsana URL se sklada proti vnitrni adrese uloziste, kterou prohlizec
 * zahodi na Mixed Content).
 */
export function mapExportPngUrl(ddCaseId: string, mapId: string, fileName: string): string {
  const query = new URLSearchParams({ download: '1', filename: fileName });
  return mediaSrc(
    `/v1/duediligence/cases/${ddCaseId}/maps/${mapId}/image?${query.toString()}`,
  );
}

/**
 * Krok QAFIX10 (QA-ZAV-N2) — EXPORT ZNA STAVOVY KOD.
 *
 * Bez nej umela obrazovka jen jednu hlasku („Zkuste to prosím znovu"), a ta
 * u VALIDACNI chyby (`400`) lhala: tatáz data poslana znovu skonci znovu
 * stejne. Panel se podle `status` rozhodne, kterou hlasku ukaze.
 */
export class ParcelExportError extends Error {
  readonly status: number;

  constructor(status: number) {
    super(`HTTP ${status}`);
    this.name = 'ParcelExportError';
    this.status = status;
  }

  /** Vada v tom, co jsme poslali — opakovani nepomuze. */
  get isInvalidRequest(): boolean {
    return this.status === 400 || this.status === 422;
  }
}

function saveBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Stahne PNG vyrezu (export `E4`). */
export async function downloadMapPng(
  ddCaseId: string,
  mapId: string,
  fileName: string,
): Promise<void> {
  const res = await fetch(mapExportPngUrl(ddCaseId, mapId, fileName), {
    credentials: 'include',
  });
  if (!res.ok) throw new ParcelExportError(res.status);
  saveBlob(await res.blob(), fileName);
}

/** Stahne PDF prehledu parcely s logem kancelare (export `J1`). */
export async function downloadParcelOverviewPdf(
  ddCaseId: string,
  input: {
    readonly parcelLabel: string | null;
    readonly rows: readonly ParcelOverviewRowInput[];
    readonly mapId?: string | undefined;
    readonly fileName: string;
  },
): Promise<void> {
  const res = await fetch(
    `${API_BASE_URL}/v1/duediligence/cases/${ddCaseId}/parcel-overview.pdf`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        parcelLabel: input.parcelLabel,
        rows: input.rows.map((row) => ({ label: row.label, value: row.value })),
        ...(input.mapId === undefined ? {} : { mapId: input.mapId }),
      }),
    },
  );
  if (!res.ok) throw new ParcelExportError(res.status);
  saveBlob(await res.blob(), input.fileName);
}
